import { SxProps } from '@mui/material';
import Table, { Column } from '../Table';
import GroupedTable from '../Table/GroupedTable';
import { GroupedData } from '../Table/types';
import useSidePanelParams from '../../hooks/useSidePanelParams';
import { UrlFilters } from '../../hooks/useUrlFilters';
import { TEST_RESULTS_TABLE_FILTERS } from '../../constants/urlParams';
import { TestResultsTableData } from './types';

type Props = {
  sx?: SxProps;
  wrapperSx?: SxProps;
  data: TestResultsTableData[];
  columns: Column<TestResultsTableData>[];
  initialSelectedFilters?: UrlFilters;
  emptyStateRenderer?: () => JSX.Element;
  scrollItemId?: string;
  /** OSS expanded row (e.g. AlertDetails). Omit in SaaS-only usages. */
  expandedRowRenderer?: (rowData: TestResultsTableData) => JSX.Element | null;
  search?: string;
  tableSearchFunction?: (search: string, data: TestResultsTableData) => boolean;
  localStorageKeyPrefix?: string;
  isLoading?: boolean;
  isFilterOptionsLoading?: boolean;
  onScrollToBottom?: () => void;
  isControlled?: boolean;
  bottomScrollPadding?: number;
  groupedData?: GroupedData<TestResultsTableData>[];
  expandedGroups?: Record<string, boolean>;
  onGroupToggle?: (groupTitle: string) => void;
  onGroupScrollToBottom?: (groupTitle: string) => void;
};

const TestResultsTable = ({
  sx,
  wrapperSx,
  data,
  columns,
  initialSelectedFilters,
  emptyStateRenderer,
  scrollItemId,
  expandedRowRenderer,
  search,
  tableSearchFunction,
  localStorageKeyPrefix,
  isLoading,
  isFilterOptionsLoading,
  onScrollToBottom,
  isControlled,
  bottomScrollPadding,
  groupedData,
  expandedGroups,
  onGroupToggle,
  onGroupScrollToBottom,
}: Props) => {
  const { openSidePanel, selectedId } = useSidePanelParams();
  const isSaaS = !!window.axiosInstance;

  const localStorageKey = localStorageKeyPrefix
    ? `${localStorageKeyPrefix}_test_results_table`
    : 'test_results_table';

  const onRowClick = isSaaS
    ? (rowData: TestResultsTableData) =>
        openSidePanel('test', rowData.subTestUniqueId, 'test')
    : undefined;

  if (groupedData) {
    return (
      <GroupedTable
        sx={sx}
        wrapperSx={wrapperSx}
        data={groupedData}
        columns={columns}
        initialSelectedFilters={initialSelectedFilters}
        idKey="subTestUniqueId"
        localStorageKey={localStorageKey}
        defaultSortKey="latestRunStatus"
        focusedRows={selectedId ? [selectedId] : []}
        search={search}
        searchFunc={tableSearchFunction}
        expandedRowRenderer={expandedRowRenderer}
        onRowClick={onRowClick}
        emptyStateRenderer={emptyStateRenderer}
        urlParamsPrefix={TEST_RESULTS_TABLE_FILTERS}
        isLoading={isLoading}
        isFilterOptionsLoading={isFilterOptionsLoading}
        isControlled={isControlled}
        bottomScrollPadding={bottomScrollPadding}
        expandedGroups={expandedGroups}
        onGroupToggle={onGroupToggle}
        onGroupScrollToBottom={onGroupScrollToBottom}
        showEmptyGroups={false}
      />
    );
  }

  return (
    <Table
      sx={sx}
      wrapperSx={wrapperSx}
      data={data}
      columns={columns}
      initialSelectedFilters={initialSelectedFilters}
      focusedRows={selectedId ? [selectedId] : []}
      search={search}
      searchFunc={tableSearchFunction}
      expandedRowRenderer={expandedRowRenderer}
      onRowClick={onRowClick}
      defaultSortKey="latestRunStatus"
      emptyStateRenderer={emptyStateRenderer}
      scrollItemId={scrollItemId}
      idKey="subTestUniqueId"
      urlParamsPrefix={TEST_RESULTS_TABLE_FILTERS}
      localStorageKey={localStorageKey}
      isLoading={isLoading}
      isFilterOptionsLoading={isFilterOptionsLoading}
      onScrollToBottom={onScrollToBottom}
      isControlled={isControlled}
      bottomScrollPadding={bottomScrollPadding}
    />
  );
};

export default TestResultsTable;
